import { ruCountryFor, ruCountryIn, ruCountryTo } from '@/i18n/countryCases';

const currentYear = () => new Date().getFullYear();

export const transport = {
    en: {
        defaultTitle: 'Transport',
    },
    ru: {
        defaultTitle: 'Транспорт',
    },
};

export const insurance = {
    en: {
        defaultTitle: 'Travel Insurance',
        metaTitleFallback: (countryName) => `Travel Insurance for ${countryName} — Compare Plans & Prices 2026`,
        metaDescFallback: (countryName) => `Compare travel insurance for ${countryName}: medical cover, trip cancellation, baggage, prices and how to buy online. Updated for 2026.`,
        whyTitle: 'Why buy travel insurance?',
        whySubtitle: 'Key reasons before every international trip',
        benefitsTitle: 'What protection includes',
        instructionTitle: 'How the insurance process works',
        instructionSubtitle: 'Simple steps to reliable coverage',
        faqTitle: 'Insurance questions answered',
        faqSubtitle: 'Everything about documents, payouts and support',
    },
    ru: {
        defaultTitle: 'Туристическая страховка',
        metaTitleFallback: (countryName) => `Страховка для ${countryName} 2026 — сравнение цен и планов`,
        metaDescFallback: (countryName) => `Сравните туристические страховки для ${countryName}: медицина, отмена поездки, багаж, цены и как оформить онлайн. Обновлено в 2026.`,
        whyTitle: 'Почему нужно оформить страховку?',
        whySubtitle: 'Главные причины перед поездкой за границу',
        benefitsTitle: 'Что входит в страховое покрытие',
        instructionTitle: 'Как получить страховой полис',
        instructionSubtitle: '5 простых шагов для надежной защиты',
        faqTitle: 'Ответы на вопросы о страховке',
        faqSubtitle: 'Все о документах, выплатах и поддержке',
    },
};

export const stay = {
    en: {
        defaultTitle: 'Accommodation',
        defaultDescription:
            'Find the perfect place to stay during your trip. Hotels, apartments, and unique accommodations worldwide.',
        citiesTitle: 'Choose a city or region',
        citiesSubtitle: 'Popular destinations with a wide selection of accommodation.',
        providersTitle: 'Booking services',
        providersSubtitle: 'Search hotels and apartments, compare conditions and go to booking.',
        searchTitle: 'Find the Best Hotels',
        searchSubtitle: 'Compare prices, locations and accommodation conditions.',
        howSubtitle: 'Choose a destination, compare options and finish the booking on the service site.',
        thematicTitle: 'Thematic Collections',
        thematicSubtitle: 'Unique collections of accommodation options',
        faqTitle: 'Frequently Asked Questions',
        faqSubtitle: 'Answers to key questions about accommodation booking',
        typesTitle: 'Types of Accommodation',
        typesSubtitle: 'Choose the right type of accommodation for your trip',
        typesLabel: 'Accommodation types',
        fallbackAdmin:
            'For administrator: Go to Sanity Studio \u2192 Service \u2192 Stay \u2192 Trip.com blocks and add blocks with accommodation types.',
        fallbackConfigMsg:
            'Configure accommodation type blocks in Sanity CMS to display on this page.',
        cityDescriptionFallback: 'Explore sections and services',
    },
    ru: {
        defaultTitle: 'Проживание',
        defaultDescription:
            'Найдите идеальное место для проживания во время путешествия. Отели, апартаменты и уникальные варианты размещения по всему миру.',
        citiesTitle: 'Выберите город',
        citiesSubtitle: 'Популярные направления с широким выбором жилья.',
        providersTitle: 'Сервисы бронирования',
        providersSubtitle: 'Ищите отели и апартаменты, сравнивайте условия и переходите к бронированию.',
        searchTitle: 'Поиск лучших отелей',
        searchSubtitle: 'Сравните цены, расположение и условия проживания.',
        howSubtitle: 'Выберите город, сравните варианты и завершите бронирование на сайте сервиса.',
        thematicTitle: 'Тематические подборки',
        thematicSubtitle: 'Уникальные подборки вариантов для проживания',
        faqTitle: 'Часто задаваемые вопросы',
        faqSubtitle: 'Ответы на ключевые вопросы о бронировании жилья',
        typesTitle: 'Типы размещения',
        typesSubtitle: 'Выберите подходящий тип размещения для вашего путешествия',
        typesLabel: 'Типы размещения',
        fallbackAdmin:
            'Для администратора: Перейдите в Sanity Studio \u2192 Service \u2192 Stay \u2192 Trip.com blocks и добавьте блоки с типами размещения.',
        fallbackConfigMsg:
            'Настройте блоки типов размещения в Sanity CMS для отображения на этой странице.',
        cityDescriptionFallback: 'Откройте разделы и сервисы',
    },
};

export const tours = {
    en: {
        defaultTitle: 'Tours and Excursions',
        defaultDescription:
            'Find the perfect experience for your trip. Compare offers from trusted providers and book online in minutes.',
        defaultHowItWorks: [
            'Pick a destination and decide what kind of experience you want.',
            'Compare curated offers from trusted tour platforms and local providers.',
            'Book the option you love and pay online with a secure checkout.',
            'Get the confirmation by email and enjoy a seamless travel experience.',
        ],
        howItWorksTitle: 'How It Works',
        howItWorksSubtitle: 'From choosing a tour to its start',
        quizTitle: 'Pick Your Tour',
        quizSubtitle: 'Take the quiz to get a personalized tour selection.',
        citiesTitle: 'City or Region:',
        citiesSubtitle: 'Tours and activities by destination.',
        providersSubtitle: 'Verified platforms with a wide selection of tours.',
        tourTypesSubtitle: 'Choose the right tour format',
        faqTitle: 'Frequently Asked Questions',
        faqSubtitle: 'Answers to key questions about tours and excursions',
        faqLinkText: 'Show all questions and answers',
        cityDescriptionFallback: 'Explore sections and services',
    },
    ru: {
        defaultTitle: 'Туры и экскурсии',
        defaultDescription:
            'Найдите идеальный тур для своего путешествия. Сравните предложения проверенных провайдеров и бронируйте онлайн.',
        defaultHowItWorks: [
            'Выберите направление и формат путешествия, который подходит именно вам.',
            'Сравните доступные предложения от проверенных платформ и организаторов экскурсий.',
            'Забронируйте понравившийся тур и оплатите онлайн безопасным способом.',
            'Получите подтверждение на почту и подготовьтесь к путешествию без хлопот.',
        ],
        howItWorksTitle: 'Как это работает',
        howItWorksSubtitle: 'От выбора тура до его начала',
        quizTitle: 'Подобрать свой тур',
        quizSubtitle: 'Пройдите опрос чтобы получить персональную подборку туров.',
        citiesTitle: 'Город или регион:',
        citiesSubtitle: 'Экскурсии и активности по направлениям.',
        providersSubtitle: 'Проверенные площадки с широким выбором туров.',
        tourTypesSubtitle: 'Выберите подходящий формат тура',
        faqTitle: 'Часто задаваемые вопросы',
        faqSubtitle: 'Ответы на ключевые вопросы о турах и экскурсиях',
        faqLinkText: 'Показать все вопросы и ответы',
        cityDescriptionFallback: 'Откройте разделы и сервисы',
    },
};

export const esim = {
    en: {
        metaTitleFallback: (countryName) => `eSIM for ${countryName} - Best Plans and Promo Codes ${currentYear()}`,
        metaDescFallback: (countryName) => `Choose the best eSIM for your trip to ${countryName}: compare plans, active promo codes, support, reviews, and installation guide.`,
        contentDescFallback: 'Choose the best eSIM for your trip: compare plans, support, reviews, and installation guide.',
        heroCta: 'View eSIM Plans',
        faqLinkText: 'Show all questions and answers',
        planDescFallback: (countryName, dataAmount, dataUnit) => `eSIM plan for ${countryName} with ${dataAmount} ${dataUnit} of data`,
        errorTitle: (country) => `eSIM for ${country}`,
        errorText: 'eSIM information for this country is temporarily unavailable.',
        errorBackLink: 'Back to home',
        scenarios: [
            {
                ix: '// a few days',
                title: 'Short Trip',
                description: 'Need internet for 3-5 days? Choose a plan with a smaller data allowance.',
                link: '#',
                cta: 'Compare Plans',
                type: 'short-trip',
            },
            {
                ix: '// a month or more',
                title: 'Long Trip',
                description: 'Choose an eSIM with more data and at least 30 days of validity.',
                link: '#',
                cta: 'Compare Plans',
                type: 'long-trip',
            },
            {
                ix: '// no data limits',
                title: 'Unlimited Internet',
                description: 'Ideal for work, streaming, and heavy use without watching your data.',
                link: '#',
                cta: 'Compare Plans',
                type: 'unlimited',
            },
            {
                ix: '// staying in touch',
                title: 'Messengers Only',
                description: 'A small data allowance for WhatsApp, Telegram, and other messaging apps.',
                link: '#',
                cta: 'Compare Plans',
                type: 'messengers',
            },
            {
                title: 'With Phone Number',
                description: 'eSIM with calling and SMS capabilities. Suitable for business trips.',
                link: '#',
                cta: 'Compare Plans',
                type: 'with-number',
            },
            {
                ix: '// multiple countries',
                title: 'Regional eSIM',
                description: 'Stay connected while travelling across several countries in the region.',
                link: '#',
                cta: 'Compare Plans',
                type: 'regional',
            },
        ],
        mobilePayScenario: {
            ix: '// easy payment',
            title: 'Apple Pay / Google Pay',
            description: 'Choose providers that support Apple Pay and Google Pay for quick checkout.',
            link: '#',
            cta: 'Compare Plans',
            type: 'mobilepay',
        },
        steps: [
            {
                title: 'Choose and Pay',
                description: 'Select a suitable plan and pay with card or PayPal',
            },
            {
                title: 'Get eSIM',
                description: 'Instantly receive QR code via email or in the app',
            },
            {
                title: 'Activate',
                description: 'Scan QR code in phone settings and start using',
            },
        ],
        fallbackFaq: (countryName) => [
            {
                question: `Why use eSIM in ${countryName}?`,
                answer: 'eSIM is a modern solution that allows you to connect to the internet in advance, without searching for a SIM card in a store or paying for expensive roaming.',
                priority: 999,
            },
            {
                question: 'What are the advantages of eSIM compared to a regular SIM?',
                answer: 'You can activate eSIM in advance, there is no need to look for a store upon arrival, and the connection is very fast.',
                priority: 999,
            },
            {
                question: 'What do you need to use eSIM?',
                answer: 'You need a smartphone with eSIM support, internet access for activation, and a bank card for payment.',
                priority: 999,
            },
            {
                question: 'How to choose the right eSIM provider?',
                answer: 'Pay attention to coverage in the required regions, internet speed, and 24/7 support availability. You can also get a personalized plan by taking a <button class="esim-quiz-link text-blue-600 hover:text-blue-800 underline font-medium">short quiz</button>.',
                isQuizLink: true,
                priority: 999,
            },
            {
                question: 'Does my phone support eSIM?',
                answer: 'Most modern smartphones support eSIM. You can check your device compatibility <button class="esim-device-check-link text-blue-600 hover:text-blue-800 underline font-medium">here</button>.',
                isDeviceCheckLink: true,
                priority: 999,
            },
            {
                question: 'How to disable eSIM after the trip?',
                answer: "Simply delete the eSIM profile in phone settings. This won't affect your main SIM card.",
                priority: 999,
            },
        ],
    },
    ru: {
        metaTitleFallback: (countryName) => `eSIM ${ruCountryFor(countryName)} - Лучшие тарифы и промокоды ${currentYear()}`,
        metaDescFallback: (countryName) => `Выбирайте лучшие eSIM для поездок ${ruCountryTo(countryName)}: сравнение тарифов, актуальные промокоды, поддержка, отзывы, инструкция по установке.`,
        contentDescFallback: 'Выбирайте лучшие eSIM для поездок: сравнение тарифов, поддержка, отзывы, инструкция по установке.',
        heroCta: 'Посмотреть тарифы eSIM',
        faqLinkText: 'Показать все вопросы и ответы',
        planDescFallback: (countryName, dataAmount, dataUnit) => `eSIM-план ${ruCountryFor(countryName)} с ${dataAmount} ${dataUnit} данных`,
        errorTitle: (country) => `eSIM ${ruCountryFor(country)}`,
        errorText: 'Информация о eSIM для этой страны временно недоступна.',
        errorBackLink: 'Вернуться на главную',
        scenarios: [
            {
                ix: '// на несколько дней',
                title: 'Короткая поездка',
                description: 'Нужен интернет на 3-5 дней? Выбирайте пакеты с небольшим объёмом трафика.',
                link: '#',
                cta: 'Сравнить тарифы',
                type: 'short-trip',
            },
            {
                ix: '// на месяц и дольше',
                title: 'Долгая поездка',
                description: 'eSIM с большим объёмом данных и сроком действия от 30 дней.',
                link: '#',
                cta: 'Сравнить тарифы',
                type: 'long-trip',
            },
            {
                ix: '// без ограничений',
                title: 'Безлимитный интернет',
                description: 'Для работы, видео и активного использования без подсчёта гигабайтов.',
                link: '#',
                cta: 'Сравнить тарифы',
                type: 'unlimited',
            },
            {
                ix: '// для общения',
                title: 'Только мессенджеры',
                description: 'Минимальный объём трафика для WhatsApp, Telegram и других мессенджеров.',
                link: '#',
                cta: 'Сравнить тарифы',
                type: 'messengers',
            },
            {
                title: 'С номером телефона',
                description: 'eSIM с возможностью звонков и SMS. Подходит для бизнес-поездок.',
                link: '#',
                cta: 'Сравнить тарифы',
                type: 'with-number',
            },
            {
                ix: '// несколько стран',
                title: 'Для соседних стран',
                description: 'Одна eSIM для поездок по нескольким странам региона.',
                link: '#',
                cta: 'Сравнить тарифы',
                type: 'regional',
            },
        ],
        mobilePayScenario: {
            ix: '// удобная оплата',
            title: 'Apple Pay / Google Pay',
            description: 'Провайдеры с оплатой через Apple Pay и Google Pay в одно касание.',
            link: '#',
            cta: 'Сравнить тарифы',
            type: 'mobilepay',
        },
        steps: [
            {
                title: 'Выбрать и оплатить',
                description: 'Выберите подходящий тариф и оплатите картой или PayPal',
            },
            {
                title: 'Получить eSIM',
                description: 'Мгновенно получите QR-код на email или в приложении',
            },
            {
                title: 'Активировать',
                description: 'Отсканируйте QR-код в настройках телефона и начните пользоваться',
            },
        ],
        fallbackFaq: (countryName) => [
            {
                question: `Зачем использовать eSIM ${ruCountryIn(countryName)}?`,
                answer: 'eSIM – это современное решение, которое позволяет подключиться к интернету заранее, не ища SIM-карту в магазине и не платя за дорогой роуминг.',
                priority: 999,
            },
            {
                question: 'Какие преимущества eSIM по сравнению с обычной SIM?',
                answer: 'Вы можете активировать eSIM заранее, не нужно искать магазин по приезде, и подключение происходит очень быстро.',
                priority: 999,
            },
            {
                question: 'Что нужно для использования eSIM?',
                answer: 'Нужен смартфон с поддержкой eSIM, доступ к интернету для активации и банковская карта для оплаты.',
                priority: 999,
            },
            {
                question: 'Как выбрать правильного провайдера eSIM?',
                answer: 'Обратите внимание на покрытие в нужных регионах, скорость интернета и наличие поддержки 24/7. Также можно получить персональный план, пройдя <button class="esim-quiz-link text-blue-600 hover:text-blue-800 underline font-medium">короткий квиз</button>.',
                isQuizLink: true,
                priority: 999,
            },
            {
                question: 'Поддерживает ли мой телефон eSIM?',
                answer: 'Большинство современных смартфонов поддерживают eSIM. Проверить совместимость вашего устройства можно <button class="esim-device-check-link text-blue-600 hover:text-blue-800 underline font-medium">здесь</button>.',
                isDeviceCheckLink: true,
                priority: 999,
            },
            {
                question: 'Как отключить eSIM после поездки?',
                answer: 'Просто удалите профиль eSIM в настройках телефона. Это не повлияет на вашу основную SIM-карту.',
                priority: 999,
            },
        ],
    },
};
